// The Planting screen (P, and first-run) — goals-first Phōkii's front door.
// A scripted, zero-AI chat: the goal, up to three expert clarifying questions,
// then an honest hand-off to the user's own LLM. The thinking happens in their
// AI; Phōkii stages the conversation and lands the plan. (GOALS-FIRST boards,
// Decisions v2 #6.)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import AI_PROMPT from '../aiPrompt';

// Bill's question bank, mined from the ten goal packs. Each question does
// motivation work before a single action exists: horizon calibration,
// implementation intentions, fresh-start framing.
const DOMAINS = [
  {
    match: /\b(weight|lbs|pounds|kilo|a1c|diet|health|fitness|sugar|blood|sleep|gym)\b/i,
    questions: [
      'Big goals fail fast and boring goals finish. What could you still see yourself doing in week nine?',
      'Plans stick when they attach to life you already live. After which daily moment — a meal, a commute, brushing your teeth — will the main habit happen?',
      'Forget the number for a second: at the end of this, what do you want to be able to *do*?',
    ],
  },
  {
    match: /\b(debt|owe|credit|money|budget|sav(e|ing)|loan|card)\b/i,
    questions: [
      "Without looking anything up: what do you think you owe (or need)? Rough is fine — naming it out loud is the first step.",
      'When, this week, is your one honest hour to find out the real numbers?',
      'Which single account or card would feel best to see at zero first?',
    ],
  },
  {
    match: /\b(run|race|10k|5k|marathon|team|soccer|tryout|varsity|train|sport|swim|bike)\b/i,
    questions: [
      'Is there a real date on the calendar — a race, a tryout, a season start? If not, should the plan pick one?',
      'Which three weekday slots are realistically yours for this?',
      "And when a session gets rained out or life eats it — what's the backup plan for that day?",
    ],
  },
  {
    match: /\b(write|novel|book|draft|app|build|launch|ship|record|album|paint|make|create|blog|start)\b/i,
    questions: [
      'What time, in what chair, does the daily work happen?',
      'You will miss a day — everyone does. What does the next morning look like?',
      'What does "done enough to show someone" look like for this?',
    ],
  },
];
const FALLBACK_QUESTIONS = [
  'By when would you love this to be true? A season is fine — it doesn\'t need a date yet.',
  'What does finished actually look like? One sentence.',
  'How many hours a week can this honestly have?',
];

// Most rows a title search will print at once. The library is 400 goals deep
// and a loose word can match most of them; past this many the page stops being
// a list and starts being a wall.
const LIB_CAP = 40;

const civilOk = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

// Accepts raw JSON, fenced JSON, or an LLM's chatty reply with an array inside.
export function parseBundles(text) {
  if (!text || !text.trim()) return { error: 'Nothing pasted yet.' };
  let t = text.trim().replace(/^```(json)?/gm, '').replace(/```/g, '');
  const start = t.indexOf('['); const end = t.lastIndexOf(']');
  if (start === -1 || end === -1 || end <= start) return { error: "Couldn't find a JSON array in that. Ask your AI for raw JSON only, then paste everything it gives you." };
  let parsed;
  try { parsed = JSON.parse(t.slice(start, end + 1)); }
  catch (e) { return { error: "That JSON didn't parse cleanly. Ask your AI to re-output the plan as raw JSON, no commentary." }; }
  if (!Array.isArray(parsed)) return { error: 'Expected a JSON array of bundles.' };
  const bundles = parsed.filter(b => b && b.project && Array.isArray(b.actions));
  if (!bundles.length) return { error: 'No usable bundles in there — each needs a "project" and an "actions" list.' };
  return { bundles };
}

export function summarizeBundles(bundles) {
  const goalNames = [...new Set(bundles.map(b => b.goal || b.objective).filter(Boolean))];
  const projects = bundles.length;
  const actions = bundles.reduce((n, b) => n + b.actions.filter(a => a && a.title).length, 0);
  return { goalNames, projects, actions };
}

const Bubble = ({ who, children }) => (
  <div className={`max-w-[85%] ${who === 'you' ? 'self-end' : 'self-start'}`}>
    <div className={`px-4 py-2.5 rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap ${
      who === 'you' ? 'themed-primary-bg text-white rounded-br-sm' : 'themed-surface themed-text border themed-border rounded-bl-sm'
    }`}>{children}</div>
  </div>
);

export default function Planting({ initialStage, onClose, onPlantBare, onPlantBundles, onSaveIntention, onOpenGoal }) {
  // stage: goal → route → question(i) → handoff → paste → preview → payoff
  // …plus 'library', a self-contained destination reachable from the route
  // door or straight from the shelf's quiet line.
  const [stage, setStage] = useState(initialStage === 'library' ? 'library' : 'goal');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [goalText, setGoalText] = useState('');
  const [answers, setAnswers] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [pasteText, setPasteText] = useState('');
  const [promptCopied, setPromptCopied] = useState(false);
  const [showRawPrompt, setShowRawPrompt] = useState(false);
  const [parsed, setParsed] = useState(null);   // { bundles } | { error }
  const [planted, setPlanted] = useState(null); // { goalId, goalName, projects, actions }
  const [intention, setIntention] = useState('');
  const [intentionSaved, setIntentionSaved] = useState(false);
  // ── the library: goals other people made ──────────────────────────────
  // Browsed in one place and nowhere else in the app. Categories → the goals
  // in a category → one goal's preview. The index is fetched once on entering;
  // a goal's own file is fetched only when it is previewed, never all 100.
  const libraryEntry = initialStage === 'library';
  const [libIndex, setLibIndex] = useState(null);   // { version, categories }
  const [libError, setLibError] = useState(null);
  const [libCat, setLibCat] = useState(null);       // an index category
  const [libQuery, setLibQuery] = useState('');
  const [libOpen, setLibOpen] = useState(null);     // { entry, cat, file } — the previewed goal
  // Live mirrors for the close handler above — it must know, without
  // re-subscribing, whether the library still has a level to step back to.
  const stageRef = useRef(stage);   stageRef.current = stage;
  const libCatRef = useRef(libCat); libCatRef.current = libCat;
  const libOpenRef = useRef(libOpen); libOpenRef.current = libOpen;
  const [libBusy, setLibBusy] = useState(false);
  const scrollRef = useRef(null);
  // Esc closes this screen. The app's global Esc cannot: it returns early
  // whenever focus sits in an input, and this screen focuses one on open.
  // BUT: while the library has somewhere to step back to, the step-back
  // handler below owns Esc. Both are capture listeners on window, so
  // stopPropagation cannot referee between them — registration order had
  // this one running first and closing the whole screen from three levels
  // deep in the library (UX-14). It now stands down explicitly.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (stageRef.current === 'library' && (libOpenRef.current || libCatRef.current)) return;
      const t = e.target;
      if (t && t.tagName === 'TEXTAREA' && (t.value || '').trim()) return; // don't discard a pasted plan
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  const inputRef = useRef(null);
  const searchRef = useRef(null);

  const domain = useMemo(() => DOMAINS.find(d => d.match.test(goalText)) || null, [goalText]);
  const questions = domain ? domain.questions : FALLBACK_QUESTIONS;

  useEffect(() => {
    // the chat grows downward; the library is a page and reads from the top
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = stage === 'library' ? 0 : scrollRef.current.scrollHeight;
  }, [messages, stage]);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, [stage]);
  // the browse position is its own scroll: a new list starts at its own top
  useEffect(() => {
    if (stage === 'library' && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [stage, libCat, libOpen]);

  // One fetch of the index, the first time the library is opened
  useEffect(() => {
    if (stage !== 'library' || libIndex || libError) return;
    let dead = false;
    fetch('/library/index.json')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('missing'))))
      .then(d => { if (!dead) setLibIndex(d); })
      .catch(() => { if (!dead) setLibError("Couldn't open the library just now."); });
    return () => { dead = true; };
  }, [stage, libIndex, libError]);

  // One goal's plan, fetched only when someone asks to look at it
  const openLibGoal = (entry, cat) => {
    setLibBusy(true); setLibError(null);
    fetch(`/library/${entry.file}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('missing'))))
      .then(file => { setLibOpen({ entry, cat, file }); setLibBusy(false); })
      .catch(() => { setLibError("Couldn't open that one just now."); setLibBusy(false); });
  };

  // Take it: planted additively, then you land on it. No questions — these
  // plans carry no dates, so there is nothing to ask about timing.
  // Two doors at the take moment (the researcher's ruling on parked books):
  // start now, or take it resting. A curiosity should never have to stand on
  // the working shelf to be owned.
  const takeLibGoal = (setAside) => {
    if (!libOpen) return;
    const result = onPlantBundles(libOpen.file.bundles, setAside ? { setAside: true } : undefined);
    if (!result) return;
    if (!setAside && result.goalId != null) onOpenGoal(result.goalId);
    onClose();
  };

  const enterLibrary = () => { setStage('library'); setLibCat(null); setLibOpen(null); setLibQuery(''); };
  const leaveLibrary = () => {
    if (libOpen) { setLibOpen(null); return; }
    if (libCat) { setLibCat(null); return; }
    if (libraryEntry) { onClose(); return; }
    setStage('route');
  };

  // Esc walks back one level of browsing; with nowhere left to go it falls
  // through to the app's own Esc, which closes the whole screen.
  useEffect(() => {
    if (stage !== 'library' || (!libOpen && !libCat)) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault(); e.stopPropagation();
      if (libOpen) setLibOpen(null); else setLibCat(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [stage, libOpen, libCat]);

  // Search runs across every goal title in the index — titles only, and the
  // hits stay in the index's own order. Nothing here is ranked or promoted.
  //
  // One letter is not a search: against 400 titles nearly every one matches,
  // and the answer is a wall of rows longer than the library itself. Two is
  // the floor, and even then the list is cut at LIB_CAP with the remainder
  // stated plainly — a cut we admit to, never a silent top-N.
  const libHits = useMemo(() => {
    const q = libQuery.trim().toLowerCase();
    if (q.length < 2 || !libIndex) return null;
    const out = [];
    for (const cat of libIndex.categories) {
      for (const g of cat.goals) if (g.title.toLowerCase().includes(q)) out.push({ cat, goal: g });
    }
    return out;
  }, [libQuery, libIndex]);

  const say = (who, text) => setMessages(m => [...m, { who, text }]);

  const composePrompt = () => {
    const lines = answers.map((a, i) => `- ${questions[i]}\n  → ${a}`).join('\n');
    return `${AI_PROMPT}\n\n---\n\nMy goal: "${goalText}"\n${answers.length ? `\nWhat I've already told you:\n${lines}\n` : ''}\nYou have everything you need — ask me nothing further. Output the plan now as the raw JSON array, no code fences, no commentary.`;
  };

  const submitGoal = () => {
    const t = input.trim(); if (!t) return;
    setGoalText(t); say('you', t); setInput('');
    say('phokii', `“${t.length > 70 ? t.slice(0, 70) + '…' : t}” — good. How do you want to plan it?`);
    setStage('route');
  };

  const startQuestions = () => {
    say('phokii', `Two or three quick things, so the plan fits your life instead of a template's.\n\n${questions[0]}`);
    setQIndex(0); setStage('question');
  };

  const submitAnswer = () => {
    const t = input.trim(); if (!t) return;
    say('you', t); setAnswers(a => [...a, t]); setInput('');
    const next = qIndex + 1;
    if (next < questions.length) { setQIndex(next); say('phokii', questions[next]); }
    else {
      say('phokii', 'That\'s everything I need to set the table.');
      setStage('handoff');
    }
  };

  const openLLM = (which) => {
    const prompt = composePrompt();
    const url = which === 'chatgpt'
      ? `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`
      : `https://claude.ai/new?q=${encodeURIComponent(prompt)}`;
    window.open(url, '_blank', 'noopener');
    setStage('paste');
  };

  // The copy button reports what actually happened and advances only on
  // success; on failure it reveals the prompt itself, selected — the one copy
  // mechanism that cannot fail. Re-copy stays available through the paste
  // stage: nothing about handing the prompt over is one-shot (UX-7).
  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(composePrompt());
      setPromptCopied(true);
      setShowRawPrompt(false);
      setTimeout(() => setPromptCopied(false), 2500);
      if (stage === 'handoff') setStage('paste');
    } catch (e) {
      setShowRawPrompt(true);   // clipboard refused — hand them the text itself
    }
  };

  const tryParse = (text) => {
    const r = parseBundles(text);
    setParsed(r);
    if (r.bundles) setStage('preview');
  };

  const plant = (bundles) => {
    const result = onPlantBundles(bundles);
    if (result) { setPlanted(result); setStage('payoff'); }
  };

  const finishToGoal = () => {
    if (intention.trim() && planted && !intentionSaved) { onSaveIntention(planted.goalId, intention.trim()); setIntentionSaved(true); }
    if (planted && planted.goalId != null) onOpenGoal(planted.goalId);
    onClose();
  };

  const summary = parsed && parsed.bundles ? summarizeBundles(parsed.bundles) : null;
  const libSummary = libOpen ? summarizeBundles(libOpen.file.bundles || []) : null;

  return (
    <div className="fixed inset-0 z-[70] themed-background flex flex-col" style={{ background: 'var(--color-background)' }}>
      <div className="flex justify-end p-4">
        <button onClick={onClose} className="text-[11px] themed-text-muted hover:themed-text border themed-border rounded-full px-3 py-1">
          {libraryEntry ? 'esc — back to your goals' : 'esc — skip to your list'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto" ref={scrollRef}>
        <div className={`${stage === 'library' ? 'max-w-3xl' : 'max-w-xl'} mx-auto px-6 pb-10`}>

          {stage === 'goal' && messages.length === 0 && (
            <div className="text-center mt-[12vh]">
              <div className="text-[10px] uppercase tracking-[0.2em] themed-text-muted font-semibold mb-4">A goal</div>
              <h1 className="text-[28px] font-serif themed-text mb-8" style={{ fontFamily: 'var(--font-display, Georgia, serif)' }}>
                What are you trying to make happen?
              </h1>
            </div>
          )}

          {messages.length > 0 && (
            <div className="flex flex-col gap-3 mt-6">
              {messages.map((m, i) => <Bubble key={i} who={m.who}>{m.text}</Bubble>)}
            </div>
          )}

          {stage === 'route' && (
            <div className="flex flex-col items-start gap-2 mt-5 ml-1">
              <button onClick={startQuestions} className="text-[13px] font-medium themed-primary hover:underline text-left">
                Plan it with my AI →
                <span className="block text-[11px] themed-text-muted font-normal">a few questions here, then your own ChatGPT or Claude writes the plan</span>
              </button>
              <button onClick={() => { say('you', "I'll plan it myself."); onPlantBare(goalText); }} className="text-[13px] font-medium themed-primary hover:underline text-left">
                Plant it bare — I'll write the plan myself →
                <span className="block text-[11px] themed-text-muted font-normal">creates the goal and opens its plan</span>
              </button>
              <button onClick={enterLibrary} className="text-[13px] font-medium themed-primary hover:underline text-left">
                Goals other people made →
                <span className="block text-[11px] themed-text-muted font-normal">written plans you can take as your own</span>
              </button>
            </div>
          )}

          {stage === 'library' && (
            // text-left is deliberate: an ancestor centres by default, and a
            // table of contents that reads as a list must be ragged-right
            <div className="mt-2 text-left">

              {/* one back link, one search field — the whole chrome */}
              <div className="flex items-baseline gap-4 flex-wrap mb-5">
                <button onClick={leaveLibrary} className="text-[11.5px] themed-text-muted hover:themed-text">
                  {libOpen ? `‹ ${libOpen.cat ? libOpen.cat.name : 'Back'}` : (libCat ? '‹ All categories' : (libraryEntry ? '‹ Your goals' : '‹ Back'))}
                </button>
                {!libOpen && (
                  <input
                    ref={searchRef}
                    value={libQuery}
                    onChange={e => { setLibQuery(e.target.value); }}
                    placeholder="Search these goals…"
                    className="ml-auto w-56 px-3 py-1.5 text-[12px] border themed-border rounded-full themed-surface themed-text focus:outline-none"
                  />
                )}
              </div>

              {libError && <p className="text-[12.5px] mb-4" style={{ color: '#c05b4d' }}>{libError}</p>}

              {/* ── the categories ── */}
              {!libOpen && !libCat && !libHits && (
                <>
                  <h2 className="text-[24px] font-serif themed-text mb-1" style={{ fontFamily: 'var(--font-display, Georgia, serif)' }}>
                    Goals other people made
                  </h2>
                  <p className="text-[12.5px] themed-text-muted mb-6" style={{ maxWidth: 460 }}>
                    Plans written out in full by people who know the ground. Take one and it becomes
                    yours to change — nothing you already have is touched.
                  </p>
                  {!libIndex && !libError && <p className="text-[12.5px] themed-text-muted">Opening…</p>}
                  {libIndex && (
                    <div className="border themed-border rounded-xl overflow-hidden">
                      {libIndex.categories.map(cat => (
                        <button key={cat.slug} onClick={() => setLibCat(cat)}
                          className="w-full text-left px-4 py-3 border-b themed-border last:border-b-0 hover:themed-surface">
                          <span className="block text-[13.5px] font-medium themed-text">{cat.name}</span>
                          <span className="block text-[12px] themed-text-muted">{cat.who}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ── the goals in one category ── */}
              {!libOpen && libCat && !libHits && (
                <>
                  <h2 className="text-[22px] font-serif themed-text mb-1" style={{ fontFamily: 'var(--font-display, Georgia, serif)' }}>
                    {libCat.name}
                  </h2>
                  <p className="text-[12.5px] themed-text-muted mb-5" style={{ maxWidth: 460 }}>{libCat.who}</p>
                  <div className="border themed-border rounded-xl overflow-hidden">
                    {libCat.goals.map(g => (
                      <button key={g.file} onClick={() => openLibGoal(g, libCat)}
                        className="w-full text-left px-4 py-3 border-b themed-border last:border-b-0 hover:themed-surface flex items-baseline gap-3">
                        <span className="flex-1 min-w-0 text-[13.5px] themed-text">{g.title}</span>
                        <span className="text-[11px] themed-text-muted whitespace-nowrap">{g.weeks} weeks</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* ── search across every goal title ── */}
              {!libOpen && libHits && (
                <>
                  <p className="text-[12.5px] themed-text-muted mb-3">
                    {libHits.length === 0
                      ? `Nothing here matches “${libQuery.trim()}”.`
                      : libHits.length > LIB_CAP
                        ? `${libHits.length} goals match “${libQuery.trim()}” — here are the first ${LIB_CAP}. Type a bit more to narrow it.`
                        : `${libHits.length} ${libHits.length === 1 ? 'goal' : 'goals'} matching “${libQuery.trim()}”`}
                  </p>
                  {libHits.length > 0 && (
                    <div className="border themed-border rounded-xl overflow-hidden">
                      {libHits.slice(0, LIB_CAP).map(h => (
                        <button key={h.goal.file} onClick={() => openLibGoal(h.goal, h.cat)}
                          className="w-full text-left px-4 py-3 border-b themed-border last:border-b-0 hover:themed-surface flex items-baseline gap-3">
                          <span className="flex-1 min-w-0">
                            <span className="block text-[13.5px] themed-text">{h.goal.title}</span>
                            <span className="block text-[11.5px] themed-text-muted">{h.cat.name}</span>
                          </span>
                          <span className="text-[11px] themed-text-muted whitespace-nowrap">{h.goal.weeks} weeks</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {libBusy && !libOpen && <p className="text-[12.5px] themed-text-muted mt-4">Opening…</p>}

              {/* ── one goal, whole: what it is, why it's built this way, and
                    the author's own contents. No row is singled out. ── */}
              {libOpen && libSummary && (
                <div>
                  <h2 className="text-[26px] font-serif themed-text leading-snug" style={{ fontFamily: 'var(--font-display, Georgia, serif)' }}>
                    {libOpen.file.name}
                  </h2>
                  {libOpen.file.pitch && (
                    <p className="text-[13.5px] themed-text mt-3 leading-relaxed" style={{ maxWidth: 620 }}>{libOpen.file.pitch}</p>
                  )}
                  <p className="text-[11.5px] themed-text-muted mt-3">
                    Written for about {libOpen.file.weeks} weeks · {libSummary.projects} project{libSummary.projects === 1 ? '' : 's'}, {libSummary.actions} action{libSummary.actions === 1 ? '' : 's'}
                  </p>

                  {libOpen.file.expert_basis && (
                    <div className="mt-5 border themed-border rounded-xl p-4" style={{ background: 'var(--color-surface)' }}>
                      <div className="text-[10px] uppercase tracking-[0.16em] themed-text-muted font-semibold mb-2">Why the plan is shaped this way</div>
                      {/* real prose, meant to be read — full contrast, quiet by size only */}
                      <p className="text-[12.5px] themed-text leading-relaxed whitespace-pre-wrap">{libOpen.file.expert_basis}</p>
                    </div>
                  )}

                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.16em] themed-text-muted font-semibold mb-3">What's in it</div>
                    {(libOpen.file.bundles || []).map((b, i) => (
                      <div key={i} className="mb-5 last:mb-0">
                        <div className="text-[13px] font-semibold themed-text pb-1.5 border-b themed-border">{b.project}</div>
                        {(b.actions || []).filter(a => a && a.title).map((a, j) => (
                          <div key={j} className="text-[12.5px] themed-text py-1 pl-3 border-b themed-border-light last:border-b-0">{a.title}</div>
                        ))}
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-3 mt-7 pb-2 flex-wrap">
                    <button onClick={() => takeLibGoal(false)} className="px-5 py-2.5 text-[13.5px] font-medium rounded-lg text-white themed-primary-bg">Start now</button>
                    <button onClick={() => takeLibGoal(true)}
                      className="px-5 py-2.5 text-[13.5px] font-medium rounded-lg border themed-border themed-text hover:themed-surface"
                      title="It becomes yours, resting at the foot of your shelf — not standing on the working row — until you pick it back up">
                      Take it, set it aside</button>
                    <button onClick={() => setLibOpen(null)} className="text-[12.5px] themed-text-muted hover:themed-text">Back</button>
                  </div>
                  <p className="text-[11.5px] themed-text-muted mt-2">
                    Either way it's yours to rewrite. Nothing you have is replaced, and you can undo it.
                  </p>
                </div>
              )}
            </div>
          )}

          {stage === 'handoff' && (
            <div className="mt-6 border themed-border rounded-xl p-5">
              <div className="text-[14px] font-semibold themed-text mb-1">Your plan is ready to be written.</div>
              <p className="text-[12.5px] themed-text-muted leading-relaxed">
                Phōkii set the table — your goal, your answers, and instructions your AI understands.
                Your own AI does the thinking, in your own account. We never see the conversation.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => openLLM('chatgpt')} className="px-4 py-2 text-[13px] font-medium rounded-lg text-white themed-primary-bg">Open in ChatGPT</button>
                <button onClick={() => openLLM('claude')} className="px-4 py-2 text-[13px] font-medium rounded-lg text-white themed-primary-bg">Open in Claude</button>
                <button onClick={copyPrompt} className="px-4 py-2 text-[13px] font-medium rounded-lg border themed-border themed-text hover:themed-surface">{promptCopied ? 'Copied ✓' : 'Copy the prompt instead'}</button>
              </div>
              <p className="text-[11px] themed-text-muted mt-3">Works with any AI — Gemini too. Paste the prompt, add nothing, copy back what it gives you.</p>
            </div>
          )}

          {(stage === 'paste' || (stage === 'preview' && parsed && parsed.error)) && (
            <div className="mt-6">
              {/* every door stays open: re-copy, reopen either AI, step back */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <button onClick={copyPrompt} className="px-3 py-1.5 text-[12px] font-medium rounded-lg border themed-border themed-text hover:themed-surface">{promptCopied ? 'Copied ✓' : 'Copy the prompt again'}</button>
                <button onClick={() => openLLM('chatgpt')} className="px-3 py-1.5 text-[12px] font-medium rounded-lg border themed-border themed-text hover:themed-surface">Open in ChatGPT</button>
                <button onClick={() => openLLM('claude')} className="px-3 py-1.5 text-[12px] font-medium rounded-lg border themed-border themed-text hover:themed-surface">Open in Claude</button>
                <button onClick={() => { setStage('handoff'); setShowRawPrompt(false); }} className="px-3 py-1.5 text-[12px] themed-text-muted hover:themed-text">‹ back</button>
              </div>
              {showRawPrompt && (
                <div className="mb-4">
                  <p className="text-[12px] themed-text-muted mb-1.5">Copying was blocked — here is the prompt itself. Select it and copy by hand:</p>
                  <textarea
                    readOnly
                    value={composePrompt()}
                    rows={6}
                    ref={el => { if (el) { el.focus(); el.select(); } }}
                    className="w-full px-3 py-2.5 text-[11.5px] font-mono border themed-border rounded-lg themed-surface themed-text focus:outline-none"
                  />
                </div>
              )}
              <div className="text-[13px] themed-text mb-2">When your AI answers, paste the whole reply here — I'll find the plan in it.</div>
              <textarea
                value={pasteText}
                onChange={e => { setPasteText(e.target.value); if (e.target.value.trim().length > 20) tryParse(e.target.value); }}
                placeholder='Paste everything — raw JSON or the full reply, either works.'
                rows={7}
                className="w-full px-3 py-2.5 text-[12.5px] font-mono border themed-border rounded-lg themed-surface themed-text focus:outline-none"
              />
              {parsed && parsed.error && pasteText.trim().length > 20 && (
                <p className="text-[12px] mt-1" style={{ color: '#c05b4d' }}>{parsed.error}</p>
              )}
            </div>
          )}

          {stage === 'preview' && summary && (
            <div className="mt-5 border themed-border rounded-xl p-4">
              <div className="text-[10.5px] uppercase tracking-wider themed-text-muted font-semibold mb-2">The plan</div>
              <div className="max-h-64 overflow-y-auto">
                {parsed.bundles.map((b, i) => (
                  <div key={i} className="mb-3 last:mb-0">
                    <div className="text-[10.5px] uppercase tracking-wider themed-text-muted font-semibold">{b.project}</div>
                    {b.actions.filter(a => a && a.title).map((a, j) => (
                      <div key={j} className="text-[12.5px] themed-text ml-2 mt-0.5">
                        · {a.title}
                        {a.nextStep && <span className="themed-text-muted"> — → {a.nextStep}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <p className="text-[12.5px] themed-text mt-3 border themed-border rounded-lg px-3 py-2" style={{ background: 'var(--color-surface)' }}>
                Adds <b>{summary.goalNames.length || 1} goal{summary.goalNames.length > 1 ? 's' : ''}, {summary.projects} project{summary.projects !== 1 ? 's' : ''}, {summary.actions} action{summary.actions !== 1 ? 's' : ''}</b> to your space.
                Nothing is replaced, nothing is scheduled that you didn't agree to — and the whole thing is undoable.
              </p>
              <div className="flex gap-3 mt-3">
                <button onClick={() => plant(parsed.bundles)} className="px-5 py-2 text-[13px] font-medium rounded-lg text-white themed-primary-bg">Plant it</button>
                <button onClick={() => { setParsed(null); setPasteText(''); setStage('paste'); }} className="px-3 py-2 text-[12.5px] themed-text-muted hover:themed-text">Paste something else</button>
              </div>
            </div>
          )}

          {stage === 'payoff' && planted && (
            <div className="mt-8 text-center">
              <div className="text-[18px] font-serif themed-text" style={{ fontFamily: 'var(--font-display, Georgia, serif)' }}>
                Your goal took shape — {planted.projects} project{planted.projects !== 1 ? 's' : ''}, {planted.actions} action{planted.actions !== 1 ? 's' : ''}.
              </div>
              {/* Nothing is suggested and nothing is dated: plantBundles sets
                  pho:false on every action and carries a doDate only if the
                  file gave one, which no library file does. Law 4. */}
              <p className="text-[13px] themed-text-muted mt-1">Nothing is starred and nothing is dated. Those marks are yours to make.</p>
              <div className="mt-6 max-w-md mx-auto text-left">
                <div className="text-[11px] uppercase tracking-wider themed-text-muted font-semibold mb-1.5">One more thing — optional</div>
                <div className="text-[13px] themed-text mb-2">When and where will this usually happen?</div>
                <input
                  ref={inputRef}
                  value={intention}
                  onChange={e => setIntention(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') finishToGoal(); }}
                  placeholder="Weekday mornings, kitchen table, before email."
                  className="w-full px-3 py-2.5 text-[13.5px] border themed-border rounded-lg themed-surface themed-text focus:outline-none"
                />
                <p className="text-[11px] themed-text-muted mt-1.5">It's written into the goal's plan — its first sentence, in your words.</p>
              </div>
              <button onClick={finishToGoal} className="mt-6 px-6 py-2.5 text-[13.5px] font-medium rounded-lg text-white themed-primary-bg">Open the goal</button>
            </div>
          )}

        </div>
      </div>

      {(stage === 'goal' || stage === 'question') && (
        <div className="border-t themed-border p-4">
          <div className="max-w-xl mx-auto flex gap-2">
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') (stage === 'goal' ? submitGoal() : submitAnswer()); }}
              placeholder={stage === 'goal' ? 'Say it however it comes out — messy is fine.' : 'Answer in your own words…'}
              className="flex-1 px-4 py-3 text-[14px] border themed-border rounded-xl themed-surface themed-text focus:outline-none"
            />
            <button onClick={stage === 'goal' ? submitGoal : submitAnswer} className="px-5 py-2 text-[13.5px] font-medium rounded-xl text-white themed-primary-bg">↵</button>
          </div>
          {stage === 'goal' && (
            <div className="max-w-xl mx-auto mt-2 flex justify-between">
              <span className="text-[11px] themed-text-muted">Get my A1C down · Ship the redesign · Run a 10K</span>
              <button onClick={() => { setGoalText(goalText || 'My goal'); setStage('paste'); }} className="text-[11px] themed-primary hover:underline">Already have a plan? Paste it →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
